import { toastManager } from '@/components/ui/toast';
import errorMessages from '@/data/err.json';

interface ValidationError {
  key: string;
  path: string;
  message: string;
  params?: Record<string, string>;
}

interface ErrorResponse {
  key?: string;
  message?: string;
  params?: Record<string, string>;
  validations?: ValidationError[];
}

const getErrorMessageFromKey = (
  key: string,
  params?: Record<string, string>
): string | null => {
  try {
    const err = (
      errorMessages as unknown as Record<string, Array<Record<string, string>>>
    )[key];

    if (!err || !Array.isArray(err)) {
      return null;
    }

    const lang = localStorage.getItem('lang') || 'en';
    const messageObj = err.find(item => item[lang]);

    if (!messageObj || !messageObj[lang]) {
      return null;
    }
    let message = messageObj[lang];

    // Params varsa replace et
    if (params && typeof params === 'object') {
      Object.keys(params).forEach(paramKey => {
        const paramValue = params[paramKey];
        if (paramValue && typeof paramValue === 'string') {
          message = message.replace(
            new RegExp(`\\{${paramKey}\\}`, 'g'),
            paramValue
          );
        }
      });
    }

    return message;
  } catch (error) {
    console.error('Error getting message from key:', error);
    return null;
  }
};

/**
 * @param error - API'den gelen hata objesi
 */
export const handleValidationErrors = (error: any) => {
  try {
    const errorResponse = error?.error as ErrorResponse | undefined;

    // { key, message, params, validations } formatını kontrol et
    if (
      errorResponse?.key &&
      errorResponse?.validations &&
      Array.isArray(errorResponse.validations)
    ) {
      // Ana hata mesajını al (örn: "someValidationErrorsOccurred")
      const mainMessage = getErrorMessageFromKey(
        errorResponse.key,
        errorResponse.params
      );

      // Validations içindeki tüm hataları işle
      const validationMessages = errorResponse.validations
        .map((validation: ValidationError) => {
          const { key, params } = validation;

          // Her validation için mesajı al ve params ile değiştir
          const validationMessage = getErrorMessageFromKey(key, params);
          return validationMessage;
        })
        .filter(Boolean); // Null olan mesajları filtrele

      // Ana mesaj + validation mesajlarını birleştir
      const finalMessage = mainMessage
        ? `${mainMessage}. ${validationMessages.join(' ')}`
        : validationMessages.join(' ');

      if (finalMessage) {
        toastManager.add({
          title: finalMessage,
          type: 'error',
        });
        return;
      }
    }

    // Sadece key varsa (validations olmadan)
    if (errorResponse?.key) {
      const { key, message, params } = errorResponse;

      // err.json'dan mesajı bul
      const translatedMessage = getErrorMessageFromKey(key, params);

      if (translatedMessage) {
        toastManager.add({
          title: translatedMessage,
          type: 'error',
        });
        return;
      }

      // Key bulunamadıysa message göster
      if (message) {
        toastManager.add({
          title: message,
          type: 'error',
        });
        return;
      }
    }

    // Fallback: Hiçbir mesaj yoksa genel hata
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    toastManager.add({
      title: fallbackMessage || 'An error occurred',
      type: 'error',
    });
  } catch (parseError) {
    console.error('Error parsing validation errors:', parseError);
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    toastManager.add({
      title: fallbackMessage || 'An error occurred',
      type: 'error',
    });
  }
};

/**
 * @param error - API'den gelen hata objesi
 */
export const handleApiError = (error: any) => {
  try {
    const actualError = error?.data || error;
    const errorResponse = actualError?.error as ErrorResponse | undefined;

    // { key, message, params, validations } formatını kontrol et
    if (
      errorResponse?.key &&
      errorResponse?.validations &&
      Array.isArray(errorResponse.validations)
    ) {
      handleValidationErrors(actualError);
      return;
    }

    // Sadece key varsa (validations olmadan)
    if (errorResponse?.key) {
      const { key, message, params } = errorResponse;

      // err.json'dan mesajı bul
      const translatedMessage = getErrorMessageFromKey(key, params);

      if (translatedMessage) {
        toastManager.add({
          title: translatedMessage,
          type: 'error',
        });
        return;
      }

      // Key bulunamadıysa message göster
      if (message) {
        toastManager.add({
          title: message,
          type: 'error',
        });
        return;
      }
    }

    // Fallback: Hiçbir mesaj yoksa genel hata
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    toastManager.add({
      title: fallbackMessage || 'An error occurred',
      type: 'error',
    });
  } catch (parseError) {
    console.error('Error handling API error:', parseError);
    const fallbackMessage = getErrorMessageFromKey('anErrorOccurred');
    toastManager.add({
      title: fallbackMessage || 'An error occurred',
      type: 'error',
    });
  }
};

/**
 * @param message - Gösterilecek mesaj key'i
 */
export const showSuccessToast = (message: string) => {
  const translatedMessage = getErrorMessageFromKey(message) || message;
  toastManager.add({
    title: translatedMessage,
    type: 'success',
  });
};

/**
 * @param message - Gösterilecek mesaj key'i
 */
export const showWarningToast = (message: string) => {
  const translatedMessage = getErrorMessageFromKey(message) || message;
  toastManager.add({
    title: translatedMessage,
    type: 'warning',
  });
};

/**
 * @param message - Gösterilecek mesaj key'i
 */
export const showInfoToast = (message: string) => {
  const translatedMessage = getErrorMessageFromKey(message) || message;
  toastManager.add({
    title: translatedMessage,
    type: 'info',
  });
};

export const getErrMsg = (error: string) => {
  const msg = getErrorMessageFromKey(error as keyof typeof errorMessages);
  return msg || error;
};
